import React, { useEffect } from 'react';
import dayjs from 'dayjs';
import Card from './styled/Card';
import { EvaluationItem as EvaluationItemCard, EvaluationItemRating } from './styled/Item';
import useLocalization from '../../../localization/useLocalization';
import { fetchEvaluationsData, fetchEvaluationsTemplatesData } from './api';
import type { EvaluationItem as EvaluationListItem, EvaluationTemplate } from './api';
import { calculateScore, formatUserName, isAutomatedEvaluation } from './utils';
import Spinner from '../../common/Spinner';
import colors from '../../../utils/colors';
import { config } from '../../../utils/utils';
import { IS_LEGACY_LISTENER_MODE } from '../../../config/electron';
import { useAppSelector } from '../../../store/hooks';
import { getAuthToken } from '../../../store/selectors/auth';
import type { RootState } from '../../../store/types';
import { openUrl } from '../../../utils/browser';

function Evaluations() {
    const authToken = useAppSelector((state: RootState) => getAuthToken(state) || '');
    const localization = useLocalization();

    const [templates, setTemplates] = React.useState<EvaluationTemplate[]>([]);
    const [isTemplatesLoading, setIsTemplatesLoading] = React.useState(false);

    const [evaluations, setEvaluations] = React.useState<EvaluationListItem[]>([]);
    const [isEvaluationLoading, setIsEvaluationLoading] = React.useState(false);

    useEffect(() => {
        setIsTemplatesLoading(true);
        fetchEvaluationsTemplatesData(authToken, {})
            .then(setTemplates)
            .catch(console.error)
            .finally(() => setIsTemplatesLoading(false));
    }, [authToken]);

    useEffect(() => {
        setIsEvaluationLoading(true);
        fetchEvaluationsData(authToken)
            .then(setEvaluations)
            .catch(console.error)
            .finally(() => setIsEvaluationLoading(false));
    }, [authToken]);

    const isLoading = isTemplatesLoading || isEvaluationLoading;

    const renderEvaluationItem = ({ item }: { item: EvaluationListItem }) => {
        const template = templates.find(t => t.id === item.templateId);

        const avgScore = template ? calculateScore(template, item.evaluation) : 0;

        const isAiEvaluation = isAutomatedEvaluation(item);

        const createdByText = isAiEvaluation
            ? localization.getText('qualityManagement.createdByAI')
            : item.createdByUser && formatUserName(item.createdByUser);

        return (
            <EvaluationItemCard
                key={item.id}
                onClick={e => {
                    e.preventDefault();

                    const dashboardUrl = config.getDashboardLink(process.env.NODE_ENV);
                    const params = encodeURIComponent(authToken);
                    const link = `${dashboardUrl}/login?token=${params}&redirect=${encodeURIComponent(`/quality-management#${item.id}`)}`;

                    openUrl(link, !IS_LEGACY_LISTENER_MODE ? '_blank' : '_self');

                    return false;
                }}>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: '4px'
                    }}>
                    <div>
                        <b>#{item.id}</b> <small>({dayjs(item.createdAt).format('DD/MM/YYYY HH:mm')})</small>
                    </div>

                    {avgScore > 0 && <EvaluationItemRating>{avgScore}</EvaluationItemRating>}
                </div>

                <div style={{ fontSize: '0.9em' }}>
                    <div style={{ fontWeight: '500', color: '#000' }}>
                        {localization.getText('qualityManagement.template')}: {template?.title ?? '-'}
                    </div>
                    <div>
                        {localization.getText('qualityManagement.createdBy')}: {createdByText}
                    </div>
                </div>
            </EvaluationItemCard>
        );
    };

    return (
        <Card>
            {isLoading ? (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center'
                    }}>
                    <Spinner $size="60px" $runnerColor={colors.blue.palatinate_blue} $backgroundColor={colors.gray.athens} />
                </div>
            ) : (
                Array.isArray(evaluations) &&
                (evaluations.length === 0 ? (
                    <div style={{ marginBottom: '1rem' }}>
                        <small>{localization.getText('qualityManagement.noEvaluations')}</small>
                    </div>
                ) : (
                    evaluations.map((item: EvaluationListItem) => {
                        return renderEvaluationItem({ item });
                    })
                ))
            )}
        </Card>
    );
}

export default Evaluations;
